import React from "react";
import { useSelector } from "react-redux";

import { Flex, Typography } from "antd";

import { makeSelectLanguage } from "../App/selectors";
import { SupportedLanguages } from "../../viewModels/Settings/TranslationModel";

type Capability = string | { label: string; items: Array<string | { label: string; items: string[] }> };

type AboutContent = {
  title: string;
  paragraphs: string[];
  capabilitiesTitle: string;
  capabilities: Capability[];
};

const ABOUT_TEXT: Record<SupportedLanguages, AboutContent> = {
  ru: {
    title: "Цифровой Логист",
    paragraphs: [
      "Цифровой Логист – это система автоматизации экспедиторского бизнеса.",
      "На российском и международном рынках экспедиционные компании сталкиваются с одной и той же проблемой — логисты перегружены и тормозят рост. Логист — это «узкое горлышко»: без него сейлы не могут быстро сформировать коммерческое предложение и заключить сделку, а сам логист не успевает просчитывать альтернативные маршруты и переиспользовать ставки, чтобы увеличить маржу, сократить время и снизить риски. Найм нового логиста — это 150 000+ рублей в РФ и месяцы ожидания. В результате теряются сделки, клиенты и деньги. В международной практике потери от слабой автоматизации логистики достигают сотен миллионов долларов в год — например, из-за задержек в просчетах и несогласованных маршрутах (по данным World Bank и BCG).",
      "ЦЛ увеличивает выработку логиста в 1,5 раза (вместо 100 калькуляций — 150, вместо 10 сделок — 15). Система автоматизирует весь цикл работы экспедиторской компании: расчёт котировок, формирование КП, подписание договоров, контроль доходов и расходов, оплаты и премии. ЦЛ анализирует тарифы, подсвечивает логисту более выгодные маршруты и похожие направления, помогает сравнивать перевозчиков. Система переиспользует актуальные прайсы и работает через API с Трансконтейнером, FESCO, Фура и др. ЦЛ обеспечивает трекинг грузов в режиме реального времени. Вся аналитика и рабочие процессы синхронизированы между международными офисами — система объединяет всю команду в единую цифровую среду. Интеграция с Битрикс24, AMO и 1С уже реализована.",
      "ЦЛ — это не просто дополнение к ERP или CRM, а цифровой фундамент для экспедиторского бизнеса, позволяющий масштабироваться без роста затрат на персонал.",
    ],
    capabilitiesTitle: "Возможности ЦЛ",
    capabilities: [
      {
        label: "Работа приложения в нескольких офисах, в том числе в разных странах",
        items: ["Китай", "РФ", "РБ", "Казахстан", "Таиланд", "Турция"],
      },
      "Ролевая модель: логист, менеджер, администратор и другие роли",
      "Веб-интерфейс на трех языках: русский, английский, китайский",
      "Запрос на котировку",
      "Копирование котировок",
      "Расчет котировки",
      "Подсказки ставок",
      "Просмотр истории ставок",
      "Автоматическое разбиение маршрута на сегменты",
      "Автоматический расчет срока перевозки",
      "Расчет НДС",
      "Расчет рентабельности калькуляции",
      "Расчет цены для клиента в различных валютах",
      "Чат логиста и менеджера в рамках каждой котировки",
      "Возврат котировки",
      "Создание КП",
      "Формирование печатной формы КП",
      "Полный цикл работы со сделкой",
      "Формирование заявок на доставку",
      "Привязка счетов к сделкам",
      "Привязка документов к сделкам",
      "Копирование счетов",
      "Формирование печатных форм счетов",
      "Формирование печатных форм актов",
      "Распознавание упаковочных листов",
      "Распределение груза по ТС",
      "Анализ аномалий в сделках",
      "Интеграция с 1С",
      "Интеграция с Битрикс",
      "Интеграция с ЦБ",
      "Интеграция с Fesco",
      "Интеграция с Трансконтейнер",
      "Справочник регионов",
      "Справочник городов",
      "Справочник стран",
      "Справочник перевозчиков",
      "Формирование реестра дополнительных услуг перевозки",
      "Управление пользователями и их правами",
      {
        label: "Отчетность",
        items: [
          "Денежные потоки",
          "Просроченная дебиторская задолженность",
          "Маржинальность бизнеса",
          "Портфель клиентов",
          "Портфель перевозчиков",
          "Новые перевозчики в котировках",
          "Эффективность сотрудников",
          "Текущие клиенты",
          "Клиенты без активности",
          "Сделки",
          "Общий анализ",
          "Валовая прибыль по менеджерам",
          "Валовая прибыль по клиентам",
        ],
      },
      {
        label: "Отслеживание грузов",
        items: [
          "Отображение местонахождения груза",
          "Планирование этапов перевозки",
          "Таймлайн перевозки с плановыми и фактическими датами исполнения",
          "Администрирование подключения к различным источникам данных",
          { label: "Интеграции", items: ["Movizor", "CargoRun", "FlightRadar", "CargoTime", "FindContainer", "Zillion"] },
        ],
      },
      "AI-агент для решения бизнес-вопросов",
      "AI-агент для автоматического заполнения котировки",
      "AI-агент для определения кода ТН ВЭД",
      "AI-планировщик размещения груза в ТС",
      "Платежный календарь",
      "Уведомления на e-mail",
      "Уведомления в Telegram",
      "Уведомления о необходимости заполнения сделок",
      "Уведомления в Битрикс",
    ],
  },
  en: {
    title: "Digital Logistician",
    paragraphs: [
      "Digital Logistician is an automation system for freight forwarding businesses.",
      "In both Russian and international markets, forwarding companies face the same problem: logistics managers are overloaded, and growth slows down. The logistics manager becomes a bottleneck: without them, sales teams cannot quickly prepare commercial offers and close deals, while the logistics manager does not have enough time to calculate alternative routes and reuse rates to increase margin, reduce time, and lower risks. Hiring a new logistics manager costs 150,000+ rubles in Russia and takes months of waiting. As a result, companies lose deals, clients, and money. In international practice, losses caused by weak logistics automation can reach hundreds of millions of dollars per year, for example because of delayed calculations and uncoordinated routes, according to the World Bank and BCG.",
      "DL increases a logistics manager's output by 1.5 times: from 100 calculations to 150, and from 10 deals to 15. The system automates the full workflow of a forwarding company: quotation calculation, commercial offer generation, contract signing, income and expense control, payments, and bonuses. DL analyzes tariffs, highlights more profitable routes and similar lanes, and helps compare carriers. The system reuses up-to-date price lists and works through APIs with TransContainer, FESCO, Fura, and others. DL provides real-time cargo tracking. All analytics and workflows are synchronized across international offices, bringing the whole team into one digital environment. Integrations with Bitrix24, AMO, and 1C are already implemented.",
      "DL is not just an add-on to ERP or CRM. It is a digital foundation for forwarding businesses, allowing them to scale without increasing personnel costs.",
    ],
    capabilitiesTitle: "DL Capabilities",
    capabilities: [
      {
        label: "Application operation across multiple offices, including offices in different countries",
        items: ["China", "Russia", "Belarus", "Kazakhstan", "Thailand", "Turkey"],
      },
      "Role model: logistician, manager, administrator, and other roles",
      "Web interface in three languages: Russian, English, and Chinese",
      "Quotation request",
      "Quotation copying",
      "Quotation calculation",
      "Rate suggestions",
      "Rate history view",
      "Automatic route splitting into segments",
      "Automatic transit time calculation",
      "VAT calculation",
      "Calculation profitability analysis",
      "Client price calculation in multiple currencies",
      "Logistician and manager chat inside each quotation",
      "Quotation return",
      "Commercial offer creation",
      "Commercial offer printable form generation",
      "Full deal lifecycle",
      "Delivery request generation",
      "Linking invoices to deals",
      "Linking documents to deals",
      "Invoice copying",
      "Printable invoice form generation",
      "Printable act form generation",
      "Packing list recognition",
      "Cargo allocation across vehicles",
      "Deal anomaly analysis",
      "Integration with 1C",
      "Integration with Bitrix",
      "Integration with the Central Bank",
      "Integration with Fesco",
      "Integration with TransContainer",
      "Regions directory",
      "Cities directory",
      "Countries directory",
      "Carriers directory",
      "Additional transportation services registry generation",
      "User and permission management",
      {
        label: "Reporting",
        items: [
          "Cash flows",
          "Overdue accounts receivable",
          "Business marginality",
          "Client portfolio",
          "Carrier portfolio",
          "New carriers in quotations",
          "Employee efficiency",
          "Current clients",
          "Clients without activity",
          "Deals",
          "General analysis",
          "Gross profit by manager",
          "Gross profit by client",
        ],
      },
      {
        label: "Cargo tracking",
        items: [
          "Cargo location display",
          "Transportation stage planning",
          "Transportation timeline with planned and actual completion dates",
          "Administration of connections to different data sources",
          { label: "Integrations", items: ["Movizor", "CargoRun", "FlightRadar", "CargoTime", "FindContainer", "Zillion"] },
        ],
      },
      "AI agent for business questions",
      "AI agent for automatic quotation completion",
      "AI agent for HS code identification",
      "AI planner for cargo placement in vehicles",
      "Payment calendar",
      "E-mail notifications",
      "Telegram notifications",
      "Notifications about required deal completion",
      "Bitrix notifications",
    ],
  },
  zh: {
    title: "数字物流师",
    paragraphs: [
      "数字物流师是一套面向货运代理业务的自动化系统。",
      "在俄罗斯和国际市场，货代公司都面临同一个问题：物流人员负荷过高，业务增长因此放缓。物流人员成为瓶颈：没有他们，销售团队无法快速生成商务报价并完成交易，而物流人员也没有足够时间计算替代路线、复用运价，以提高利润率、缩短时间并降低风险。在俄罗斯，招聘一名新的物流人员需要 150,000 卢布以上，并且往往要等待数月。结果是交易、客户和资金的流失。在国际实践中，由于物流自动化薄弱造成的损失每年可能达到数亿美元，例如报价延迟和路线协调不足造成的损失，相关情况可见 World Bank 和 BCG 的数据。",
      "数字物流师可将物流人员产能提升 1.5 倍：从 100 次计算提升到 150 次，从 10 笔交易提升到 15 笔。系统自动化货代公司的完整工作周期：报价计算、商务报价生成、合同签署、收入和支出控制、付款和奖金。系统分析运价，向物流人员提示更有利的路线和相似方向，并帮助比较承运人。系统复用最新价目表，并通过 API 与 TransContainer、FESCO、Fura 等系统对接。数字物流师提供实时货物跟踪。所有分析和工作流程在国际办公室之间同步，将整个团队连接到统一的数字环境中。与 Bitrix24、AMO 和 1C 的集成已经实现。",
      "数字物流师不仅是 ERP 或 CRM 的补充，而是货代业务的数字化基础，使企业能够在不增加人员成本的情况下扩展规模。",
    ],
    capabilitiesTitle: "数字物流师功能",
    capabilities: [
      {
        label: "支持多个办公室协同工作，包括不同国家的办公室",
        items: ["中国", "俄罗斯", "白俄罗斯", "哈萨克斯坦", "泰国", "土耳其"],
      },
      "角色模型：物流人员、经理、管理员及其他角色",
      "三种语言的网页界面：俄语、英语、中文",
      "报价请求",
      "复制报价",
      "报价计算",
      "运价提示",
      "查看运价历史",
      "自动将路线拆分为多个区段",
      "自动计算运输时效",
      "增值税计算",
      "计算利润率分析",
      "以多种货币计算客户价格",
      "每个报价内的物流人员与经理聊天",
      "退回报价",
      "创建商务报价",
      "生成商务报价打印表单",
      "完整的交易工作周期",
      "生成配送申请",
      "将账单关联到交易",
      "将文件关联到交易",
      "复制账单",
      "生成账单打印表单",
      "生成验收单打印表单",
      "装箱单识别",
      "按车辆分配货物",
      "交易异常分析",
      "与 1C 集成",
      "与 Bitrix 集成",
      "与中央银行集成",
      "与 Fesco 集成",
      "与 TransContainer 集成",
      "地区目录",
      "城市目录",
      "国家目录",
      "承运人目录",
      "生成附加运输服务登记表",
      "用户及权限管理",
      {
        label: "报表",
        items: [
          "现金流",
          "逾期应收账款",
          "业务利润率",
          "客户组合",
          "承运人组合",
          "报价中的新承运人",
          "员工效率",
          "当前客户",
          "无活动客户",
          "交易",
          "综合分析",
          "按经理统计毛利润",
          "按客户统计毛利润",
        ],
      },
      {
        label: "货物跟踪",
        items: [
          "显示货物位置",
          "规划运输阶段",
          "包含计划和实际完成日期的运输时间线",
          "管理与不同数据源的连接",
          { label: "集成", items: ["Movizor", "CargoRun", "FlightRadar", "CargoTime", "FindContainer", "Zillion"] },
        ],
      },
      "用于解决业务问题的 AI 代理",
      "用于自动填写报价的 AI 代理",
      "用于确定 HS 编码的 AI 代理",
      "用于规划车辆内货物摆放的 AI 规划器",
      "付款日历",
      "电子邮件通知",
      "Telegram 通知",
      "需要填写交易信息的通知",
      "Bitrix 通知",
    ],
  },
};

const renderCapability = (capability: Capability) => {
  if (typeof capability === "string") {
    return <li key={capability}>{capability}</li>;
  }

  return (
    <li key={capability.label}>
      {capability.label}
      <ul>
        {capability.items.map((item) =>
          typeof item === "string" ? (
            <li key={item}>{item}</li>
          ) : (
            <li key={item.label}>
              {item.label}
              <ul>
                {item.items.map((subItem) => (
                  <li key={subItem}>{subItem}</li>
                ))}
              </ul>
            </li>
          ),
        )}
      </ul>
    </li>
  );
};

function AboutPage() {
  const language = useSelector(makeSelectLanguage());
  const content = ABOUT_TEXT[language] ?? ABOUT_TEXT.ru;

  return (
    <Flex vertical gap="middle">
      <Typography.Title>{content.title}</Typography.Title>
      {content.paragraphs.map((paragraph) => (
        <Typography.Paragraph key={paragraph}>{paragraph}</Typography.Paragraph>
      ))}

      <Typography.Title level={2}>{content.capabilitiesTitle}</Typography.Title>
      <ul>{content.capabilities.map(renderCapability)}</ul>
    </Flex>
  );
}

export default React.memo(AboutPage);
